import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { BackIcon } from '../components/icons.js';
import type { ContextVersionSummary, MemberRole, ProjectContextVersion } from '@jabberr/core';
import { Markdown } from '../components/Markdown.js';
import { ErrorBox, Spinner } from '../components/ui.js';
import { api, errorMessage } from '../lib/api.js';
import { relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';
import { useSession } from '../state/session.js';

function draftKey(projectId: string): string {
  return `jabberr.context-draft.${projectId}`;
}

function parseLayer(search: string): 'internal' | 'client' | null {
  const params = new URLSearchParams(search);
  const val = params.get('layer');
  if (val === 'internal' || val === 'client') return val;
  return null;
}

export function ContextPage() {
  const { projectId = '' } = useParams();
  const navigate = useNavigate();
  const drawer = useDrawer();
  const { user, refreshProjects } = useSession();
  const [latest, setLatest] = useState<ProjectContextVersion | null>(null);
  const [versions, setVersions] = useState<ContextVersionSummary[]>([]);
  const [viewing, setViewing] = useState<ProjectContextVersion | null>(null);
  const [draft, setDraft] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'edit' | 'preview'>('edit');
  const [layer, setLayer] = useState<'internal' | 'client'>('internal');
  const [guestRole, setGuestRole] = useState<MemberRole | null>(null);
  const [loading, setLoading] = useState(true);

  const isGuest = guestRole === 'guest';
  const effectiveLayer = isGuest ? 'client' : layer;

  const isClientEmpty = effectiveLayer === 'client' && !latest?.audience || (latest?.audience === 'client' && latest.version === 0);

  const load = useCallback(async () => {
    try {
      const project = await api.projects.get(projectId);
      setGuestRole(project.role ?? null);
    } catch {
      // ignore - will show as not found on subsequent errors
    }
    await loadContext();
  }, [projectId]);

  const loadContext = async () => {
    try {
      const res = await api.context.get(projectId, effectiveLayer);
      setLatest(res.latest);
      setVersions(res.versions);
      setViewing(res.latest);
      setDraft((prev) => {
        if (prev !== null && prev !== res.latest.content) {
          return res.latest.content;
        }
        return prev ?? res.latest.content;
      });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const parsed = parseLayer(window.location.search);
    setLayer(parsed ?? 'internal');
  }, []);

  useEffect(() => {
    setLatest(null);
    void load();
  }, [load, effectiveLayer]);

  useEffect(() => {
    drawer.setInfo({ projectId, title: 'Project notes', ...(latest ? { contextVersion: latest.version } : {}) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, latest?.version]);

  useEffect(() => {
    if (draft === null) return;
    try {
      if (latest && draft === latest.content) localStorage.removeItem(draftKey(projectId));
      else localStorage.setItem(draftKey(projectId), draft);
    } catch {
      // ignore
    }
  }, [draft, latest, projectId]);

  const setUrlLayer = (l: 'internal' | 'client'): void => {
    const params = new URLSearchParams(window.location.search);
    params.set('layer', l);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const switchLayer = (l: 'internal' | 'client'): void => {
    setLayer(l);
    setUrlLayer(l);
  };

  const view = async (v: number): Promise<void> => {
    if (latest && v === latest.version) {
      setViewing(latest);
      return;
    }
    try {
      const { version } = await api.context.version(projectId, v, effectiveLayer);
      setViewing(version);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const publish = async (): Promise<void> => {
    if (draft === null) return;
    setBusy(true);
    setError(null);
    try {
      const body = { content: draft, ...(note.trim() ? { note: note.trim() } : {}), audience: effectiveLayer };
      const { version } = await api.context.publish(projectId, body as Parameters<typeof api.context.publish>[1]);
      setLatest(version);
      setViewing(version);
      setVersions((prev) => [{ ...version, content: undefined } as unknown as ContextVersionSummary, ...prev.filter((p) => p.version !== version.version)]);
      setNote('');
      try {
        localStorage.removeItem(draftKey(projectId));
      } catch {
        // ignore
      }
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const dirty = latest !== null && draft !== null && draft !== latest.content;

  const renderEmptyState = (): React.ReactNode => {
    if (effectiveLayer === 'internal') return null;
    return <p className="muted small">Nothing is shared with the client yet.</p>;
  };

  const layerOptions: { key: 'internal' | 'client'; label: string }[] = [
    { key: 'internal', label: 'Team only' },
    { key: 'client', label: 'What clients are told' },
  ];

  return (
    <div className="page context-page">
      <header className="page-head">
        <div>
          <Link to={`/p/${projectId}/knows`} className="back-link">
          <BackIcon />
          What Jabberr knows
        </Link>
          <h1>Project notes</h1>
          <p className="muted small">
            The goal, constraints, decisions and open questions every new conversation starts from. Publishing makes a new version;
            a conversation already under way keeps the version it started with.
          </p>
        </div>
        {latest ? <span className={`chip`}>version {latest.version}</span> : null}
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}

      {loading ? (
        <Spinner label="Loading" />
      ) : (
        <div className="context-grid">
          <section className="card editor">
            {/* Layer switch as segmented control */}
            {!isGuest && (
              <div className="segmented segment-layer" role="radiogroup" aria-label="Context layer">
                {layerOptions.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`segment-item ${layer === opt.key ? 'active' : ''}`}
                    onClick={() => void switchLayer(opt.key)}
                    role="radio"
                    aria-checked={layer === opt.key}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Layer description */}
            <p className="muted small">
              {effectiveLayer === 'internal'
                ? 'Only the team sees this. It is part of every internal conversation.'
                : 'Guests and clients see this. It is part of their conversations and of the team.'}
            </p>

            {/* Empty state for client layer */}
            {renderEmptyState()}

            {/* Editor section (hidden for guests) */}
            {!isGuest && (
              <>
                <div className="section-head">
                  <h2>Draft</h2>
                  <div className="segmented segment-mode">
                    <button
                      type="button"
                      className={`segment-item ${mode === 'edit' ? 'active' : ''}`}
                      onClick={() => setMode('edit')}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className={`segment-item ${mode === 'preview' ? 'active' : ''}`}
                      onClick={() => setMode('preview')}
                    >
                      Preview
                    </button>
                  </div>
                </div>
                {mode === 'edit' ? (
                  <textarea
                    className="editor-area"
                    value={draft ?? ''}
                    onChange={(e) => setDraft(e.target.value)}
                    spellCheck
                    aria-label="Context draft"
                  />
                ) : (
                  <div className="preview-area">
                    <Markdown text={draft || '_Empty_'} />
                  </div>
                )}
                <div className="row wrap publish-row">
                  <input
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Note for this version (optional)"
                    maxLength={500}
                    aria-label="Version note"
                  />
                  <button type="button" className="btn btn-primary" onClick={() => void publish()} disabled={busy || !dirty}>
                    Publish version {latest?.version ? latest.version + 1 : 1}
                  </button>
                  {dirty ? (
                    <button type="button" className="link-btn" onClick={() => setDraft(latest?.content ?? draft)}>
                      Discard changes
                    </button>
                  ) : latest ? (
                    <span className="publish-row-status">No changes since version {latest.version}.</span>
                  ) : null}
                </div>
              </>
            )}
          </section>

          <aside className="card versions">
            <h2>Versions</h2>
            <ul className="list compact">
              {versions.map((v) => (
                <li key={v.version} className={`list-item ${viewing?.version === v.version ? 'active' : ''}`}>
                  <button type="button" className="link-btn" onClick={() => void view(v.version)}>
                    Version {v.version}
                  </button>
                  <span className="muted tiny">
                    {relativeTime(v.publishedAt)}
                    {v.note ? ` · ${v.note}` : ''}
                    {v.audience === 'client' ? ' · clients see this' : ''}
                  </span>
                </li>
              ))}
            </ul>
            {viewing ? (
              <div className="version-view">
                <div className="section-head">
                  <h3>Version {viewing.version}</h3>
                  {!isGuest && viewing.version !== (latest?.version ?? 0) ? (
                    <button type="button" className="link-btn small" onClick={() => setDraft(viewing.content)}>
                      Load into draft
                    </button>
                  ) : null}
                </div>
                <div className="preview-area small-preview">
                  <Markdown text={viewing.content || '_Empty_'} />
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      )}
    </div>
  );
}
